<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'
import { clearStoredOrgOverride, getStoredOrgOverride } from '~/utils/orgOverride'

// Async on purpose: PanelLayout is the whole authenticated app chrome
// (IconRail, FileExplorer, ChatPanel + their dependency graph). This layout is
// shared with the public marketing pages (/, /blog, /research, …), which only
// render the bare <slot/> below — a static import would drag the entire app
// shell into the layout chunk every marketing visitor downloads. The async
// component's chunk is only fetched when <PanelLayout> actually renders
// (i.e. on /app routes, where it still SSRs and hydrates normally).
const PanelLayout = defineAsyncComponent(() => import('~/components/shell/PanelLayout.vue'))

const route = useRoute()
const { t } = useI18n()
const { user, loading, waitForAuth } = useAuth()
const { profile, ensure: ensureProfile, fetchProfile } = useUserProfile()

const isAppRoute = computed(() => route.path.startsWith('/app'))
const showChrome = computed(() => isAppRoute.value && route.path !== '/app/setup')

/**
 * SSR found no verified session but a session hint (MAR-1421, `plugins/auth-ssr.ts`) and is leaving
 * the decision to the client. The loading shell stays up until this layout has mounted, so the
 * hydrated render matches the server's even though the client has already restored the user by then.
 */
const authDeferred = useState<boolean>('auth-deferred', () => false)

// Eagerly load user profile during SSR if auth exists
useLazyAsyncData('profile', async () => {
  if (isAppRoute.value && user.value) {
    await ensureProfile()
  }
  return true
})

// Client-side session restoration backup: on /app routes, ensure the user profile is loaded
// once Firebase Auth has settled (and re-read if the browser holds an org override header).
onMounted(async () => {
  authDeferred.value = false
  if (!isAppRoute.value) return
  await waitForAuth()
  if (!user.value) return
  const stored = getStoredOrgOverride()
  if (!profile.value || stored !== (profile.value.org_override_id ?? null)) {
    await ensureProfile()
  }
})

/**
 * Exiting is now a purely local act: the override lives in this browser and rides on the
 * `x-marcus-org-override` header, so dropping it stops the impersonation on the very next request —
 * no round trip, and nothing left server-side to get out of step (MAR-427).
 */
async function exitOrgOverride() {
  clearStoredOrgOverride()
  await fetchProfile()
}
</script>

<template>
  <div v-if="isAppRoute && ((loading && !user) || authDeferred)" class="flex items-center justify-center min-h-screen bg-background">
    <div class="flex flex-col items-center gap-2">
      <Loader2 class="h-8 w-8 animate-spin text-primary" />
      <span class="text-xs text-muted-foreground">{{ t('common.loadingApp') }}</span>
    </div>
  </div>
  <template v-else>
    <!-- App routes: the override banner sits ABOVE the app bar in flow, not over it — the bar's right
         edge carries the Activity trigger, and a fixed banner covered exactly that. This wrapper owns
         the viewport height; PanelLayout fills what is left. -->
    <div v-if="showChrome" class="h-viewport flex flex-col overflow-hidden">
      <div
        v-if="profile?.org_override_id"
        class="shrink-0 border-b border-destructive/50 bg-destructive/85 px-4 py-2 text-center text-sm font-semibold text-destructive-foreground"
      >
        <span>
          {{ t('orgOverride.banner', { org: profile.org_override_name || profile.organization_name }) }}
        </span>
        <button
          type="button"
          class="ml-3 rounded border border-destructive-foreground/50 px-2 py-0.5 text-xs hover:bg-destructive-foreground/15"
          @click="exitOrgOverride"
        >
          {{ t('orgOverride.exit') }}
        </button>
      </div>

      <PanelLayout class="flex-1 min-h-0">
        <slot />
      </PanelLayout>
    </div>

    <!-- Non-app routes (landing, login, setup): no chrome. /app/setup is an app route without the
         shell, so it keeps the overlay form of the banner. -->
    <template v-else>
      <div
        v-if="isAppRoute && profile?.org_override_id"
        class="fixed inset-x-0 top-0 z-50 border-b border-destructive/50 bg-destructive/85 px-4 py-2 text-center text-sm font-semibold text-destructive-foreground shadow-lg backdrop-blur-sm"
      >
        <span>
          {{ t('orgOverride.banner', { org: profile.org_override_name || profile.organization_name }) }}
        </span>
        <button
          type="button"
          class="ml-3 rounded border border-destructive-foreground/50 px-2 py-0.5 text-xs hover:bg-destructive-foreground/15"
          @click="exitOrgOverride"
        >
          {{ t('orgOverride.exit') }}
        </button>
      </div>
      <slot />
    </template>
  </template>
</template>
